import dayjs from "dayjs";
import { KeyboardEvent as KeyboardEventReact } from "react";

import { ALL_OVERVIEW_DATA, DAYJS_FORMAT, DAYJS_NICE_FORMAT_DATE, DAYJS_NICE_FORMAT_TIME, DAYJS_WITH_TIME_FORMAT, DEFAULT_MAP_POSITION, DEFAULT_UPLOAD_PREVIEW_SIZE, DEFAULT_ZOOM, EMPTY_DATA_CONTENT_CATEGORY, GENERATIONS, KEYS, LENKA_PERMS, MIN_DATE_LENKA, MIN_DATE_MAKUDEROVI, MIN_DATE_ROMAN, MIN_YEARS, PERMS, ROMAN_PERMS, ROUTES, SEARCH_TABS_URL, TRACK_COLORS_BY_TYPE, ZODIAC_SIGNS } from "~/const-files/const";
import { IVisitJson, IEvent, IGalleryItem, IParseCoordsData, IBEVisitItem, ICalendarEvent, IAddressItem, IPersonItem, IGallery, TTrackType, TCoords, TService, IGuessData, IGuessGalleryItem, IDebugStoreData, IDebugLine, IZipFileItem, IEventDayItem, ITrackDistances, TSearchTab } from "~/interfaces";
import { ISearchItem } from "~/providers/search";
import { IMapPosition } from "~/map-interfaces";
import { ALL_DATABASE_ITEMS_CATEGORY } from "~/database-items/provider";

import { decode, isValid } from "./olc";
import { getCurrentWeekData } from "~/providers/month-data";
import { addNotification } from "~/stores/notifications";
import { IInfoLineItem } from "~/components/InfoLine";
import { getGuessGalleries } from "~/providers/events";
import { TAGS } from "~/const-files/tags";
import { getPersonsData } from "~/persons/persons";
import { addJpegExif, getWebpExif } from "~/utils/exif";

export function isLocalhost() {
	return location.hostname.indexOf("localhost") !== -1;
}

/* eslint-disable no-magic-numbers */
export function timeToSeconds(value: string): number {
	const parts = value.split(":");

	if (parts.length === 2) {
		return (parseFloat(parts[0]) * 60) + parseFloat(parts[1]);
	}

	return 0;
}


export function sortArrayNumbers(array: Array<number>): Array<number> {
	array.sort((aItem, bItem) => aItem - bItem);

	return array;
}

export function getRandomHexHash(): string {
	return Math.random().toString(16).replace("0.", "");
}

// <min; max)
export function getRandomArbitrary(min: number, max: number) {
	const minCeiled = Math.ceil(min);
	const maxFloored = Math.floor(max);

	return Math.floor((Math.random() * (maxFloored - minCeiled + 1)) + minCeiled);
}

export function getRandomList(min: number, max: number, len: number, sort?: boolean) {
	const list: Array<number> = [];

	while (list.length !== Math.min(len, max - min + 1)) {
		const newNumber = getRandomArbitrary(min, max);

		if (!list.includes(newNumber)) {
			list.push(newNumber);
		}
	}

	if (sort) {
		sortArrayNumbers(list);
	}

	return list;
}

interface IIncludeArrayItems {
	inpArray: Array<string>;
	testArray: Array<string>;
	// testujeme na stejnou delku poli
	lenCheck?: boolean;
	// testujeme na to, ze vstupni pole obsahuje testovaci pole - cele, jinak pouze cast
	fullCheck?: boolean;
}

export function includeArrayItems({
	inpArray,
	testArray,
	lenCheck = false,
	fullCheck = true,
}: IIncludeArrayItems) {
	if (lenCheck && inpArray.length !== testArray.length) {
		return false;
	}

	let checks = 0;

	for (const testValue of testArray) {
		if (inpArray.includes(testValue)) {
			checks++;
		}
	}

	return fullCheck
		? checks === testArray.length
		: checks > 0;
}

export function getMonthDayDesc({
	date,
	month = true,
	weekDay = false,
}: {
	date: string;
	month?: boolean;
	weekDay?: boolean;
}): string {
	return new Intl.DateTimeFormat("cs", { ...month ? { month: 'long' } : {}, day: 'numeric', ...weekDay ? { weekday: 'long' } : {} }).format(dayjs(date).toDate());
}

export function getFullDesc(date: string): string {
	return new Intl.DateTimeFormat("cs", { year: 'numeric', month: 'long', day: 'numeric' }).format(dayjs(date).toDate());
}

export function getShortMonthDayDate(date: string): string {
	return new Intl.DateTimeFormat("cs", { month: 'short', day: 'numeric' }).format(dayjs(date).toDate());
}

export function getShortEventTitleDate(event: IEvent) {
	const datePart = [
		getShortMonthDayDate(event.date),
		...event.toDate
			? [
				getShortMonthDayDate(event.toDate),
			]
			: [],
	].join(" - ");

	return `${datePart} ${event.title}`;
}

export function getDateRange(dateStr: string, toDateStr: string, weekDay?: boolean): string {
	const date = dayjs(dateStr);
	const toDate = dayjs(toDateStr);
	const fromMonth = date.get("month");
	const toMonth = toDate.get("month");

	if (fromMonth === toMonth) {
		const toPart = getMonthDayDesc({
			date: toDateStr,
			weekDay,
		});

		if (weekDay) {
			const fromPart = getMonthDayDesc({
				date: dateStr,
				month: false,
				weekDay,
			});

			return `${fromPart} - ${toPart}`;
		}

		return `${dayjs(date).format("D.")} - ${toPart}`;
	} else if (date.get("year") === toDate.get("year")) {
		if (weekDay) {
			return `${getMonthDayDesc({
				date: dateStr,
				weekDay,
			})} - ${getMonthDayDesc({
				date: toDateStr,
				weekDay,
			})}`;
		}

		return `${dayjs(date).format("D. M.")} - ${dayjs(toDate).format("D. M.")}`;
	}

	return `${dayjs(date).format("M/YYYY")} - ${dayjs(toDate).format("M/YYYY")}`;
}

export function getDateFromEvent(event: IEvent, weekDay?: boolean) {
	return event.toDate
		? getDateRange(event.date, event.toDate, weekDay)
		: getMonthDayDesc({
			date: event.date,
			weekDay,
		});
}

export function getClassName(classes: Array<string>): string {
	const filtered = classes.filter(item => item.length > 0);

	return filtered.join(" ");
}

export function removeFromArray<T>(inputArray: Array<T>, compareFn: (value: T) => boolean): Array<T> {
	const ind = inputArray.findIndex(compareFn);

	if (ind !== -1) {
		const copiedArray = inputArray.slice();

		copiedArray.splice(ind, 1);

		return copiedArray;
	}

	return inputArray;
}

export function insertUnique<T>(inputArray: Array<T>, uniqueItem: T): Array<T> {
	if (inputArray.includes(uniqueItem)) {
		return inputArray;
	}

	return [
		...inputArray,
		uniqueItem,
	];
}

export function transVideosCount(count: number) {
	if (count >= 2 && count <= 4) {
		return `${count} videa`;
	} else if (count === 1) {
		return `${count} video`;
	}

	return `${count} videií`;
}

export function transPhotosCount(count: number) {
	if (count >= 2 && count <= 4) {
		return `${count} fotky`;
	} else if (count === 1) {
		return `${count} fotka`;
	}

	return `${count} fotek`;
}

export function transYears(years: number) {
	if (years >= 2 && years <= 4) {
		return `${years} roky`;
	} else if (years === 1) {
		return `${years} rok`;
	}

	return `${years} let`;
}

export function transYearsBack(years: number) {
	return `-${transYears(years)}`;
}

export function transTrackType(type: TTrackType) {
	switch (type) {
		case "bike":
			return "kolo";

		case "boat":
			return "loď";

		case "cable":
			return "lanovka";

		case "car":
			return "auto";

		case "pubt":
			return "mhd";

		case "walk":
			return "chůze";

		case "train":
			return "vlak";

		default:
	}

	return "chybí";
}

export function getYearsBack(eventYear = 0, curYear = 0, useText = false) {
	const yearsBack = curYear - eventYear;
	const yearsBackStr = yearsBack > 0
		? useText
			? ` (${transYearsBack(yearsBack)})`
			: ` (-${yearsBack})`
		: "";

	return `${eventYear}${yearsBackStr}`;
}

export function getLenDesc(photosCount: number, videosCount: number) {
	if (photosCount > 0 && videosCount > 0) {
		return `${transPhotosCount(photosCount)}, ${transVideosCount(videosCount)}`;
	}

	if (photosCount > 0) {
		return transPhotosCount(photosCount);
	}

	if (videosCount > 0) {
		return transVideosCount(videosCount);
	}

	return "";
}

export function replaceRoute(path: string, replaceObj: {[key: string]: string;}) {
	let output = path;
	const keys = Object.keys(replaceObj);

	for (const key of keys) {
		output = output.replace(key, replaceObj[key]);
	}

	return output;
}

export function getDataLink(folder?: string) {
	const source = ROUTES.OVERVIEW;
	const year = folder ?? dayjs().get("year").toString();

	return replaceRoute(source, {
		":year": year,
	});
}

export function getSearchLink(tab: TSearchTab, params?: URLSearchParams) {
	const usp = new URLSearchParams({
		tab: SEARCH_TABS_URL[tab],
	});

	params?.forEach((value, key) => key !== "tab" && usp.append(key, value));

	return `${ROUTES.SEARCH}?${usp.toString()}`;
}

export function getSearchTabFromUrl(value: string): TSearchTab {
	const tabs = Object.keys(SEARCH_TABS_URL) as Array<TSearchTab>;

	return tabs.find(tab => SEARCH_TABS_URL[tab] === value) ?? null;
}

export function getSearchPhotosLink(persons: Array<Pick<IPersonItem, "key">>) {
	return getSearchLink("photos", new URLSearchParams({
		persons: persons.map(person => person.key.toLowerCase()).join(","),
	}));
}

export function valueSplitByDem(value = "", splitter = "-") {
	const ind = value.indexOf(splitter);

	return {
		leftPart: ind === -1 ? value : value.slice(0, ind),
		rightPart: ind === -1 ? "" : value.slice(ind + 1),
	};
}

export function parseSeo(seo: string) {
	const parts = valueSplitByDem(seo);

	return {
		folderSeo: parts.leftPart,
		nameSeo: parts.rightPart,
	};
}

function parseDates(folderName: string, fromDate?: string, toDate?: string) {
	if (fromDate) {
		const fromDateObj = dayjs(fromDate);
		const yearStr = fromDateObj.get("year").toString() === folderName
			? ""
			: `${fromDateObj.get("year")}-`;

		if (toDate) {
			const toDateObj = dayjs(toDate);
			const toYearStr = fromDateObj.get("year") === toDateObj.get("year")
				? ""
				: `${toDateObj.get("year")}-`;

			return `${yearStr}${fromDateObj.format("MM-DD")}-${toYearStr}${toDateObj.format("MM-DD")}`;
		}

		return `${yearStr}${fromDateObj.format("MM-DD")}`;
	}

	return "";
}

export function getGalleryLink(seo: string, fromDate?: string, toDate?: string) {
	const parsed = parseSeo(seo);
	let folderSeo = parsed.folderSeo;
	const datesSeo = parseDates(parsed.folderSeo, fromDate, toDate);

	if (datesSeo) {
		folderSeo = `${folderSeo}-${datesSeo}`;
	}

	return replaceRoute(ROUTES.GALLERY, {
		":folderSeo": folderSeo,
		":nameSeo": parsed.nameSeo,
	});
}

export function getGalleryDetailLink(folderSeo: string, nameSeo: string, detailImage?: string) {
	return detailImage
		? replaceRoute(ROUTES.GALLERY_DETAIL, {
			":folderSeo": folderSeo,
			":nameSeo": nameSeo,
			":detailImage": detailImage,
		})
		: replaceRoute(ROUTES.GALLERY, {
			":folderSeo": folderSeo,
			":nameSeo": nameSeo,
		});
}

export function getCalendarDateLink(dateStr: string) {
	const date = dayjs(dateStr).format(DAYJS_FORMAT);

	return `${ROUTES.CALENDAR}?date=${date}`;
}

export function getDataContentLink(category?: string) {
	return replaceRoute(ROUTES.DATA_CONTENT, {
		":category": category || EMPTY_DATA_CONTENT_CATEGORY,
	});
}

export function getDatabaseItemsLink(category?: string) {
	return replaceRoute(ROUTES.DATABASE_ITEMS, {
		":category": category || ALL_DATABASE_ITEMS_CATEGORY,
	});
}

export function getTracksLink(galleryYear: string) {
	const source = ROUTES.TRACKS;
	const year = galleryYear ?? dayjs().get("year");
	const yearStr = year === "0"
		? ALL_OVERVIEW_DATA.URL
		: year.toString();

	return replaceRoute(source, {
		":year": yearStr,
	});
}

export function formatTime(seconds: number) {
	const minutes = seconds / 60 >>> 0;
	const restSeconds = seconds - (minutes * 60) >>> 0;
	const minStr = minutes.toString().padStart(2, "0");
	const secStr = restSeconds.toString().padStart(2, "0");

	return `${minStr}:${secStr}`;
}

export function debounce(func: (...rest: any[]) => void, wait: number) {
	let timeout = 0;

	return function debounced(...params: any[]) {
		function later() {
			timeout = 0;
			func(...params);
		}

		clearTimeout(timeout);
		timeout = window.setTimeout(later, wait);
	};
}

export function getZoom(searchItem: ISearchItem) {
	return searchItem.type === "poi"
		? 17
		: 13;
}

export function numberToDec(number: number, dec = 5) {
	return parseFloat(number.toFixed(dec));
}

export function getIdFromGalleryItem(data: IGalleryItem) {
	return `${data.eventSeo || "no-seo"}-${data.name}`;
}

export function getPhotoByDirection(curItem: IGalleryItem, items: Array<IGalleryItem>, direction: number) {
	const curId = getIdFromGalleryItem(curItem);
	const ind = items.findIndex(item => getIdFromGalleryItem(item) === curId);
	const maxInd = items.length - 1;
	let newInd = ind + direction;

	if (newInd < 0) {
		newInd = maxInd;
	} else if (newInd > maxInd) {
		newInd = 0;
	}

	return items[newInd];
}

export async function downloadImageJPG(imageSrc: string, fileName: string) {
	const image = await fetch(imageSrc);
	const imageBlog = await image.blob();
	const imageURL = URL.createObjectURL(imageBlog);
	const link = document.createElement("a");

	link.href = imageURL;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(imageURL);
}

function canvasToJpegBlob(imageSrc: string, quality: number): Promise<Blob | null> {
	return new Promise(resolve => {
		const img = new Image();

		//img.crossOrigin = "anonymous";

		img.addEventListener("load", () => {
			const canvas = document.createElement("canvas");

			canvas.width = img.naturalWidth;
			canvas.height = img.naturalHeight;

			const ctx = canvas.getContext("2d");

			if (!ctx) {
				resolve(null);

				return;
			}

			ctx.drawImage(img, 0, 0);

			// Convert canvas to JPG blob
			canvas.toBlob(blob => {
				if (!blob) {
					resolve(null);

					return;
				}

				resolve(blob);
			}, "image/jpeg", quality);
		});
		img.addEventListener("error", () => {
			resolve(null);
		});
		img.src = imageSrc;
	});
}

// exif z webp se prenese do jpegu (canvas ho zahodi)
export async function imageToJpegBlob(imageSrc: string, quality = 0.92): Promise<Blob | null> {
	let src = imageSrc;
	let exif: Uint8Array | null = null;

	try {
		const request = await fetch(imageSrc);

		if (request.ok) {
			const imageBlob = await request.blob();

			exif = getWebpExif(await imageBlob.arrayBuffer());
			src = URL.createObjectURL(imageBlob);
		}
	} catch (exc) {
		// fotka se nacte primo pres img, jen bez exifu
	}

	const jpeg = await canvasToJpegBlob(src, quality);

	if (src !== imageSrc) {
		URL.revokeObjectURL(src);
	}

	return jpeg && exif
		? addJpegExif(jpeg, exif)
		: jpeg;
}

export async function downloadImageWEBP(imageSrc: string, fileName: string, quality = 0.92) {
	const blob = await imageToJpegBlob(imageSrc, quality);

	if (blob) {
		const url = URL.createObjectURL(blob);

		// Trigger download
		const link = document.createElement("a");

		link.href = url;
		link.download = fileName.replace("webp", "jpg");
		document.body.appendChild(link);
		link.click();
		link.remove();

		URL.revokeObjectURL(url);
	}
}

export function downloadImage(imageSrc: string, fileName: string) {
	// na localhostu getData.php posila soubor primo z disku - stahneme ho beze zmeny (bez prevodu webp -> jpg)
	if (imageSrc.toLocaleLowerCase().includes("webp") && !isLocalhost()) {
		return downloadImageWEBP(imageSrc, fileName);
	}

	return downloadImageJPG(imageSrc, fileName);
}

export function downloadJson(json: any, fileName: string) {
	const blob = new Blob([JSON.stringify(json, null, "\t")]);
	const jsonUrl = URL.createObjectURL(blob);
	const link = document.createElement("a");

	link.href = jsonUrl;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(jsonUrl);
}

export function getEventTitle(event: IEvent, customTitle?: string, weekDay?: boolean) {
	if (customTitle) {
		return customTitle;
	}

	const dateObj = dayjs(event.date);
	const year = dateObj.get("year");
	const yearsDiff = dayjs().diff(dateObj, "years");
	const yearsStr = yearsDiff > 0
		? `, ${transYearsBack(yearsDiff)}`
		: ``;

	return `${event.title} (${getDateFromEvent(event, weekDay)} ${year}${yearsStr})`;
}

function getMapPosition(lon: number, lat: number): IMapPosition {
	return {
		x: lon,
		y: lat,
		z: DEFAULT_ZOOM,
	};
}

export function createEmptyDistances(): ITrackDistances {
	return {
		walk: 0,
		car: 0,
		boat: 0,
		bike: 0,
		cable: 0,
		pubt: 0,
		train: 0,
		all: 0,
	};
}

/** Stred obalky vsech bboxu - nahrada za maplibregl.LngLatBounds, aby se maplibre nedostala do hlavniho bundlu */
export function getBBoxesCenter(bboxes: Array<[TCoords, TCoords]>): TCoords {
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;

	for (const bbox of bboxes) {
		for (const [lng, lat] of bbox) {
			minLng = Math.min(minLng, lng);
			minLat = Math.min(minLat, lat);
			maxLng = Math.max(maxLng, lng);
			maxLat = Math.max(maxLat, lat);
		}
	}

	return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}

export function getMapPositionFromEvent(event: IEvent): IMapPosition {
	if (event.allTracks.length) {
		const [lng, lat] = getBBoxesCenter(event.allTracks.map(track => track.bbox));

		return getMapPosition(lng, lat);
	} else if (event.coords) {
		return getMapPosition(event.coords[0], event.coords[1]);
	}

	return DEFAULT_MAP_POSITION;
}

export function getMapPositionFromYearItems(items: Array<IGallery>) {
	const filtered = items.filter(item => !!item.event.coords);

	if (filtered.length) {
		const coords = filtered[0].event.coords;

		return getMapPosition(coords[0], coords[1]);
	}

	return DEFAULT_MAP_POSITION;
}

export function getMapPositionFromYearGallery(gallery: IGallery) {
	const filtered = gallery.items.filter(item => !!item.coords);

	if (filtered.length) {
		const coords = filtered[0].coords;

		return getMapPosition(coords[0], coords[1]);
	}

	return getMapPositionFromEvent(gallery.event);
}

export function getTrackColorByType(type: TTrackType) {
	if (type in TRACK_COLORS_BY_TYPE) {
		return TRACK_COLORS_BY_TYPE[type];
	}

	// def
	return "#000";
}

export function getTrackPatternByType(type: TTrackType) {
	const color = getTrackColorByType(type);

	return `line-${color}`;
}

export function formatDistance(distance: number) {
	if (distance < 1000) {
		return `${distance} m`;
	}

	const formatted = (distance / 1000).toFixed(1);

	return `${formatted} km`;
}

export function parseCoords(text: string, defZoom = DEFAULT_ZOOM): IParseCoordsData {
	let lon = 0;
	let lat = 0;
	let zoom = defZoom;
	let type: IParseCoordsData["type"] = "error";

	// souradnice
	const match = text.match(/\d+\.?\d*\s*,\s*\d+\.?\d*/u);

	if (match) {
		const parts = match[0].split(",").map(item => parseFloat(item));
		const hasBrackets = text.indexOf("[") !== -1 && text.indexOf("]") !== -1;

		lon = hasBrackets
			? parts[0]
			: parts[1];
		lat = hasBrackets
			? parts[1]
			: parts[0];
		type = "lon-lat";
	} else if (text.indexOf("+") !== -1) {
		const olcStr = text.trim().split(" ")[0];

		if (isValid(olcStr)) {
			try {
				const olc = decode(text.trim());

				lon = olc.longitudeLo;
				lat = olc.latitudeLo;
				type = "olc";
			} catch (exc) {
				/* eslint-disable-next-line */
				console.log(exc);
			}
		} else {
			/* eslint-disable-next-line */
			console.log(`Not valid str ${olcStr}`);
		}
	} else if (text.indexOf("mapy.com") !== -1) {
		try {
			const url = new URL(text);

			lon = parseFloat(url.searchParams.get("x"));
			lat = parseFloat(url.searchParams.get("y"));
			zoom = parseFloat(url.searchParams.get("z"));
			type = "mapycom";
		} catch (exc) {
			/* eslint-disable-next-line */
			console.log(exc);
		}
	}

	return {
		type,
		coords: [lon, lat],
		zoom,
	};
}

export async function imgToArrayBufferWEBP(url: string) {
	const blob = await imageToJpegBlob(url);

	if (blob) {
		try {
			const arrayBuffer = await blob.arrayBuffer();

			return arrayBuffer;
		} catch (err) {
			/* eslint-disable-next-line */
			console.log(err);
		}
	}

	return null;
}

export async function imageToArrayBuffer(src: string): Promise<ArrayBuffer> {
	try {
		const request = await fetch(src);
		const data = await request.arrayBuffer();

		return data;
	} catch (exc) {
		/* eslint-disable-next-line */
		console.log(exc);

		return null;
	}
}

export function imgToArrayBuffer(url: string) {
	if (url.toLocaleLowerCase().includes("webp")) {
		return imgToArrayBufferWEBP(url);
	}

	return imageToArrayBuffer(url);
}

export function numberAtPercent(index: number, leftValue: number, rightValue: number) {
	const wholePoint = index >>> 0;
	const diff = index - wholePoint;

	if (diff < 0.05) {
		return leftValue;
	}

	const partA = leftValue * (1 - diff);
	const partB = rightValue * diff;
	const value = partA + partB;

	return value >>> 0;
}

export function loadImage(src: string, minTime?: number): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const startTime = Date.now();

		img.addEventListener("load", () => {
			const diff = Date.now() - startTime;

			if (minTime > 0 && minTime > diff) {
				setTimeout(() => {
					resolve(img);
				}, minTime - diff);
			} else {
				resolve(img);
			}
		});
		img.addEventListener("error", () => reject());
		img.src = src;
	});
}

export function getImage(src: string, crossOrigin?: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();

		img.addEventListener("load", () => {
			resolve(img);
		});
		img.addEventListener("error", () => {
			reject(img);
		});

		if (crossOrigin) {
			img.crossOrigin = crossOrigin;
		}

		img.src = src;
	});
}

export function getAspectSize(size: number, imgWidth: number, imgHeight: number) {
	let width = 0;
	let height = 0;

	if (imgWidth > imgHeight) {
		width = imgWidth / imgHeight * size >>> 0;
		height = size;
	} else {
		width = size;
		height = imgHeight / imgWidth * size >>> 0;
	}

	return {
		width,
		height,
	};
}

export function getVideoCover(videoSrc: string, size = DEFAULT_UPLOAD_PREVIEW_SIZE): Promise<string> {
	return new Promise(resolve => {
		const video = document.createElement('video');

		function snapImage() {
			const canvas = document.createElement('canvas');
			const aspectSize = getAspectSize(size, video.videoWidth, video.videoHeight);

			canvas.crossOrigin = "anonymous";
			canvas.width = aspectSize.width;
			canvas.height = aspectSize.height;
			canvas.getContext('2d')?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, canvas.width, canvas.height);

			// 100x100
			const check = canvas.width * canvas.height > 10000;
			let error = false;
			let dataUrl = "";

			try {
				dataUrl = canvas.toDataURL();
			} catch (exc) {
				error = true;
			}

			return {
				image: check && !error
					? dataUrl
					: "",
				check,
				error,
			};
		}

		const timeupdate = () => {
			const siDate = snapImage();

			if (!siDate.check) {
				return;
			}

			if (siDate.error) {
				resolve("");
			} else {
				video.removeEventListener('timeupdate', timeupdate);
				video.src = "";
				resolve(siDate.image);
			}
		};

		video.addEventListener('loadeddata', () => {
			timeupdate();
		});
		video.addEventListener('timeupdate', timeupdate);
		video.preload = 'metadata';
		video.src = videoSrc;
		// Load video in Safari / IE11
		video.muted = true;
		video.playsInline = true;
		video.play();
	});
}

export function getFileCover(file: File, size = DEFAULT_UPLOAD_PREVIEW_SIZE): Promise<string> {
	return new Promise((resolve, reject) => {
		const fileReader = new FileReader();

		if (file.type.match('image')) {
			fileReader.onload = async () => {
				try {
					const img = await loadImage(fileReader.result as string);
					const canvas = document.createElement('canvas');
					const aspectSize = getAspectSize(size, img.naturalWidth, img.naturalHeight);

					canvas.width = aspectSize.width;
					canvas.height = aspectSize.height;
					canvas.getContext('2d')?.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, canvas.width, canvas.height);

					try {
						const dataUrl = canvas.toDataURL();

						resolve(dataUrl);
					} catch (exc) {
						resolve("");
					}
				} catch (exc) {
					reject();
				}
			};
			fileReader.onerror = () => {
				reject();
			};
			fileReader.readAsDataURL(file);
		} else {
			fileReader.onload = () => {
				if (fileReader.result) {
					const blob = new Blob([fileReader.result], { type: file.type });
					const url = URL.createObjectURL(blob);

					getVideoCover(url).then(cover => {
						URL.revokeObjectURL(url);
						resolve(cover);
					});
				} else {
					reject();
				}
			};
			fileReader.onerror = () => {
				reject();
			};
			fileReader.readAsArrayBuffer(file);
		}
	});
}

export function deferPromise<T>() {
	let resolveCb: (data: T) => void = null;
	let rejectCb: (error: string) => void = null;
	const promise: Promise<{ data: T, error: string; }> = new Promise(resolve => {
		resolveCb = (data: T) => {
			resolve({
				data,
				error: "",
			});
		};
		rejectCb = (error: string) => {
			resolve({
				data: null,
				error,
			});
		};
	});

	return {
		promise,
		resolveCb,
		rejectCb,
	};
}

export function getVisitJsonItem(): IVisitJson {
	return {
		lang: navigator.language,
		agent: navigator.userAgent,
		screenWidth: screen.width,
		screenHeight: screen.height,
	};
}

export function compareVisitJsonToBrowser(browser: IVisitJson, jsonItem: IVisitJson) {
	return browser.lang === jsonItem.lang
		&& browser.agent === jsonItem.agent
		&& browser.screenWidth === jsonItem.screenWidth
		&& browser.screenHeight === jsonItem.screenHeight;
}

export function getVisitItem(admin: boolean): Omit<IBEVisitItem, "ip" | "id" | "client" | "sameClient"> {
	return {
		date: dayjs().format(DAYJS_WITH_TIME_FORMAT),
		mobile: screen.orientation.type === "portrait-primary" || screen.orientation.type === "portrait-secondary",
		json: JSON.stringify(getVisitJsonItem()),
		admin,
	};
}

export function splitFile(file: File, chunkSizeMB: number) {
	const fileSize = file.size;
	const chunkSize = chunkSizeMB * Math.pow(1024, 2);

	if (fileSize < chunkSize) {
		return [file];
	}

	const chunksLen = Math.ceil(fileSize / chunkSize);
	let start = 0;
	let end = chunkSize;
	const chunks = [];

	for (let ind = 0; ind < chunksLen; ind++) {
		const chunk = file.slice(start, end);

		chunks.push(chunk);
		start = end;
		end = start + chunkSize;
	}

	return chunks;
}

export function formatSize(size: number) {
	if (typeof size !== "number") {
		return "";
	}

	let lv = size > 0
		? Math.floor(Math.log(size) / Math.log(1024))
		: 0;
	const sizes = ["", "K", "M", "G", "T"];

	lv = Math.min(sizes.length, lv);

	const value = lv > 0 ? (size / Math.pow(1024, lv)).toFixed(2) : size;

	return `${value} ${sizes[lv]}B`;
}

export function formatNumberToThousands(value: number, unit?: boolean) {
	const strValue = value.toString();
	const parts = strValue.split(".");

	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/ug, ' ');

	return `${parts.join(".")}${unit ? " Kč" : ""}`;
}

export function calcPriceBeforeDiscount(priceAfterDisc: number, discount: number) {
	const perPercent = priceAfterDisc / (100 - discount);

	return perPercent * 100;
}

export function getEasterSunday(year = dayjs().get("year")) {
	// konstanty pro gregoriansky kalendar
	const mVal = 24;
	const nVal = 5;
	// vypocet
	const aVal = year % 19;
	const bVal = year % 4;
	const cVal = year % 7;
	const dVal = ((19 * aVal) + mVal) % 30;
	const eVal = (nVal + (2 * bVal) + (4 * cVal) + (6 * dVal)) % 7;

	const date = dayjs(`${year}-03-22`).add(dVal + eVal, "days");

	return date.format(DAYJS_FORMAT);
}

interface IYearHolidayItem {
	title: string;
	date: string;
	short: string;
}

export function getYearHolidays(year: number) {
	const holidays: Array<IYearHolidayItem> = [];
	const easterSun = getEasterSunday(year);
	const easterMon = dayjs(easterSun).add(1, "days").format(DAYJS_FORMAT);
	const easterFriday = dayjs(easterSun).subtract(2, "days").format(DAYJS_FORMAT);

	holidays.push({
		title: "Nový rok zav.",
		date: `${year}-01-01`,
		short: "NR",
	}, {
		title: "Velký pátek otev.",
		date: easterFriday,
		short: "VP",
	}, {
		title: "Velikonoční pondělí zav.",
		date: easterMon,
		short: "VE",
	}, {
		title: "Svátek práce otev.",
		date: `${year}-05-01`,
		short: "SP",
	}, {
		title: "Den vítězství zav.",
		date: `${year}-05-08`,
		short: "DV",
	}, {
		title: "Cyril & Metoděj otev.",
		date: `${year}-07-05`,
		short: "CM",
	}, {
		title: "Upálení Jana Husa otev.",
		date: `${year}-07-06`,
		short: "JH",
	}, {
		title: "Den české státnosti zav.",
		date: `${year}-09-28`,
		short: "SV",
	}, {
		title: "Den vzniku Československa zav.",
		date: `${year}-10-28`,
		short: "VC",
	}, {
		title: "Den boje za svobodu a demokracii otev.",
		date: `${year}-11-17`,
		short: "SD",
	}, {
		title: "Štědrý den otev. dop.",
		date: `${year}-12-24`,
		short: "ŠD",
	}, {
		title: "1. svátek vánoční zav.",
		date: `${year}-12-25`,
		short: "V1",
	}, {
		title: "2. svátek vánoční zav.",
		date: `${year}-12-26`,
		short: "V2",
	});

	return holidays;
}

export function getStateHolidays(year = dayjs().get("year")) {
	const event: ICalendarEvent = {
		title: "",
		date: "",
		extendedProps: {
			seo: "",
			public: true,
			groups: [],
			stateHoliday: true,
		},
		classNames: ["my-event", "calendar-event", "state-holiday"],
	};
	const holidays = getYearHolidays(year);
	const events: Array<ICalendarEvent> = holidays.map(item => ({
		...event,
		title: item.title,
		date: item.date,
	}));

	return events;
}

export function getCurrentMonday() {
	const date = dayjs();
	const curDate = date.get("day");
	let days = curDate - 1;

	if (curDate === 1) {
		days = 0;
	} else if (curDate === 0) {
		days = 6;
	}

	return date.subtract(days, "days");
}

export function getInfoLine({
	messengerNews = false,
	uploadedFileNews = false,
}: {
	messengerNews?: boolean;
	uploadedFileNews?: boolean;
}) {
	const allMsgs: Array<IInfoLineItem> = [];
	const monday = getCurrentMonday();
	const data = getCurrentWeekData(monday);

	if (data.length) {
		allMsgs.push({
			text: "Tento týden",
		});

		data.forEach(dataItem => {
			const items: Array<string> = [];
			const month = dataItem.diffMonth
				? ` ${dataItem.month + 1}.`
				: "";

			dataItem.items.forEach(oneItem => {
				switch (oneItem.type) {
					case "birthdate":
						// v den narozeni misto "nar. 0"
						items.push(oneItem.age === 0
							? `${oneItem.person} - <b>narození</b>`
							: `${oneItem.person} - nar. <b>${oneItem.age}</b>`);
						break;

					case "calendar":
						items.push(oneItem.text);
						break;

					case "holidate":
						items.push(`${oneItem.person} - svát.`);
						break;

					default:
				}
			});

			allMsgs.push({
				text: `<b>${dataItem.date}.${month}</b> ${items.join(", ")}`,
				active: dataItem.active,
				past: dataItem.past,
			});
		});
	} else {
		allMsgs.push({
			text: "Tento týden žádné události",
		});
	}

	if (uploadedFileNews) {
		allMsgs.push({
			text: "nově nahrané fotky",
		});
	}

	if (messengerNews) {
		allMsgs.push({
			text: "nové zprávy v messengeru",
		});
	}

	return allMsgs;
}

export function copyToClipboard(text: string, msg = "Zkopírováno do schránky") {
	navigator.clipboard.writeText(text);
	addNotification(msg);
}

export function getAddressString(address: IAddressItem) {
	if (!address.streetName || address.zipCode === 0) {
		return address.city;
	}

	const zipCodeStr = address.zipCode.toString();
	const zipCode = `${zipCodeStr.slice(0, 3)} ${zipCodeStr.slice(3, 5)}`;
	const commaItems = [
		address.firm || "",
		address.building || "",
		`${address.streetName} ${address.houseNumber}`,
		`${zipCode} ${address.city}`,
	].filter(item => item).join(", ");

	return commaItems;
}

export function getPersonsTitle(persons: Array<IPersonItem>) {
	const filtered = persons.filter(person => "familyName" in person);

	if (persons.length > 1 && filtered.length > 0) {
		const names = persons.map(person => person.fullName.split(" ")[0]).join(", ");

		return `${names} ${filtered[0].familyName}`;
	}

	return persons.map(person => person.fullName).join(", ");
}

export function hasTimeHours(time: string) {
	return time && time.length > MIN_DATE_ROMAN.length;
}

export function getGalleryItemTitle(galleryItem: IGalleryItem, gallery: IGallery, showOnlyHours = false) {
	if (!galleryItem.date || !gallery) {
		return "";
	}

	const dateObj = dayjs(galleryItem.date);
	const hasTime = hasTimeHours(galleryItem.date);

	if (showOnlyHours && !hasTime) {
		return "";
	}

	let format = "";

	if ((gallery.dateType === "same" && !gallery.event.toDate) || showOnlyHours) {
		format = hasTime
			? "H:mm"
			: "";
	} else if (gallery.dateType === "same-month") {
		format = hasTime
			? "D. H:mm"
			: "D.";
	} else if (gallery.dateType === "more-months" || (gallery.dateType === "same" && gallery.event.toDate)) {
		format = hasTime
			? "D. M. H:mm"
			: "D. M.";
	} else {
		// more years
		format = hasTime
			? DAYJS_NICE_FORMAT_TIME
			: DAYJS_NICE_FORMAT_DATE;
	}

	return format
		? dateObj.format(format)
		: "";
}

export function getDateWithHours(dateStr: string) {
	const hasTime = hasTimeHours(dateStr);
	const format = hasTime
		? "D. M. H:mm"
		: "D. M.";

	return dayjs(dateStr).format(format);
}

export function getDateHours(dateStr: string) {
	const hasTime = hasTimeHours(dateStr);

	return hasTime
		? dayjs(dateStr).format("H:mm")
		: "";
}

export function getFullDateWithHours(dateStr: string, dayOfWeek = false) {
	const hasTime = hasTimeHours(dateStr);
	const format = hasTime
		? DAYJS_NICE_FORMAT_TIME
		: DAYJS_NICE_FORMAT_DATE;

	return dayjs(dateStr).format(`${dayOfWeek ? "dd " : ""}${format}`);
}

export function formatDuration(duration: number) {
	let curTime = duration;
	const hours = Math.floor(curTime / 3600);

	curTime -= hours * 3600;

	const minutes = Math.floor(curTime / 60);

	curTime -= minutes * 60;

	const seconds = curTime;

	if (hours > 0) {
		return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
	}

	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function getPersonGeneration(year: number) {
	const filtered = GENERATIONS.filter(item => year >= item.fromYear && year <= item.toYear);

	return filtered.length > 0
		? filtered[0].title
		: "unknown";
}

export function getPersonZodiadSign(birthdate: dayjs.Dayjs) {
	const date = birthdate.get("date");
	const month = birthdate.get("month") + 1;

	for (let ind = 0, max = ZODIAC_SIGNS.length; ind < max; ind++) {
		const sign = ZODIAC_SIGNS[ind];

		if (sign.month !== month) {
			continue;
		}

		if (date >= sign.date) {
			return sign.name;
		}

		const prevSign = ind > 0
			? ZODIAC_SIGNS[ind - 1]
			: ZODIAC_SIGNS[ZODIAC_SIGNS.length - 1];

		return prevSign.name;
	}

	return "";
}

export function isKeyEnter(event: KeyboardEvent | KeyboardEventReact) {
	return event.code === KEYS.ENTER || event.code === KEYS.NUM_ENTER;
}

export function getDistanceBetweenPointsWGS84(wgs84a: TCoords, wgs84b: TCoords, altitude = 0) {
	const alt = 6371009 + altitude;
	let dx = Math.abs(wgs84a[0] - wgs84b[0]);
	let dy = Math.abs(wgs84a[1] - wgs84b[1]);

	dx = dx * Math.PI / 180;
	dy = dy * Math.PI / 180;

	const y1 = wgs84a[1] * Math.PI / 180;
	const y2 = wgs84b[1] * Math.PI / 180;
	const aVal = (Math.sin(dy/2) * Math.sin(dy/2)) + (Math.cos(y1) * Math.cos(y2) * Math.sin(dx/2) * Math.sin(dx/2));
	const cVal = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1-aVal));

	return alt * cVal;
}

export function getDistanceWGS84(wgs84Coords: Array<TCoords>) {
	let distance = 0;

	for (let ind = 0, max = wgs84Coords.length - 2; ind <= max; ind++) {
		distance += getDistanceBetweenPointsWGS84(wgs84Coords[ind], wgs84Coords[ind + 1]);
	}

	return distance >>> 0;
}

export function getGroupTitle(event: IEvent, service: TService) {
	if (event.public) {
		return "Veřejné";
	}

	if (event.groups) {
		const titles: Array<{ full: string; short: string; }> = [];

		if (service === "makuderovi") {
			if (event.groups.includes(PERMS.RODINA)) {
				titles.push({
					full: "Rodina",
					short: "ROD",
				});
			}

			if (event.groups.includes(PERMS.DETI)) {
				titles.push({
					full: "Děti",
					short: "DĚT",
				});
			}
		} else if (service === "roman") {
			if (event.groups.includes(ROMAN_PERMS.KAMOSI)) {
				titles.push({
					full: "Kámoši",
					short: "KÁM",
				});
			}

			if (event.groups.includes(ROMAN_PERMS.SEZNAM)) {
				titles.push({
					full: "Seznam",
					short: "SEZ",
				});
			}

			if (event.groups.includes(ROMAN_PERMS.RODINA)) {
				titles.push({
					full: "Rodina",
					short: "ROD",
				});
			}
		} else if (service === "lenka") {
			if (event.groups.includes(LENKA_PERMS.RODINA)) {
				titles.push({
					full: "Rodina",
					short: "ROD",
				});
			}
		}

		return titles.length === 1
			? titles[0].full
			: titles.map(item => item.short).join(", ");
	}

	return "";
}

export function getImagePersons({
	persons = [],
	names = [],
	short = false,
}: {
	persons: Array<IPersonItem>;
	names?: Array<string>;
	short?: boolean;
}) {
	const selPersons = persons.length
		? persons
		: getPersonsData(names);
	const output: Array<string> = [];

	for (const person of selPersons) {
		const parts = person.fullName.split(" ");
		let outputPerson = person.fullName;

		if (parts.length > 0) {
			if (short) {
				outputPerson = parts.map(item => item[0]).join("");
			} else {
				outputPerson = parts.map(item => item.slice(0, 3)).join(" ");
			}
		}

		output.push(outputPerson);
	}

	return output.join(", ");
}

export function sortStringArray(array: Array<string>) {
	array.sort((aItem, bItem) => aItem.localeCompare(bItem));

	return array;
}

export function sortStringArrayWithName<T>(array: Array<T>, name: keyof T) {
	array.sort((aItem, bItem) => (aItem[name] as string).localeCompare(bItem[name] as string));

	return array;
}

interface IGetRandomGalleryByTags {
	count?: number;
	months?: boolean;
	tracks?: boolean;
	only?: "all" | "holidays";
}

function getRandomGalleryByTags({
	count = 5,
	months = true,
	tracks = true,
	only = "all",
}: IGetRandomGalleryByTags) {
	const allGalleries = getGuessGalleries();
	const tagsObj: {[key: string]: Array<IGallery>; } = {};
	const othersGalleries: Array<IGallery> = [];
	const monthTagsGalleries: Array<IGallery> = [];
	const tracksGalleries: Array<IGallery> = [];

	for (const gallery of allGalleries) {
		if (gallery.type === "month-item") {
			monthTagsGalleries.push(gallery);
		}

		if (gallery.event.tags?.length) {
			for (const tag of gallery.event.tags) {
				if (!(tag in tagsObj)) {
					tagsObj[tag] = [];
				}

				tagsObj[tag].push(gallery);
			}
		} else if (gallery.event.allTracks.length > 0) {
			tracksGalleries.push(gallery);
		} else {
			othersGalleries.push(gallery);
		}
	}

	// nechceme tagy o 1. polozce
	const allTags = Object.keys(tagsObj);

	for (const tag of allTags) {
		if (tagsObj[tag].length < 2) {
			for (const item of tagsObj[tag]) {
				othersGalleries.push(item);
			}

			delete tagsObj[tag];
		}
	}

	// pridame dalsi
	tagsObj.othersTag = othersGalleries;

	if (months) {
		tagsObj.monthTag = monthTagsGalleries;
	}

	if (tracks) {
		tagsObj.tracksTag = tracksGalleries;
	}

	const curTags = Object.keys(tagsObj);
	const selTagInd = getRandomArbitrary(0, curTags.length - 1);
	// "Narozeniny Martínek"; - galerie kde jsou roky, mesice a dny
	//const selTag = "Základka sraz";
	const selTag = curTags[selTagInd];
	let tagGalleries: Array<IGallery> = tagsObj[selTag];

	if (only === "holidays") {
		tagGalleries = tagsObj[TAGS.DOVOLENA];
	}

	const selGalleries: Array<IGuessGalleryItem> = [];
	const randomList = getRandomList(0, tagGalleries.length - 1, count);
	const dates: {[key: string]: Array<IGuessGalleryItem>;} = {};

	for (const ind of randomList) {
		const gallery = tagGalleries[ind];
		const dateObj = dayjs(gallery.event.date);
		const yearMonthStr = dateObj.format("YYYY-MM");

		if (!(yearMonthStr in dates)) {
			dates[yearMonthStr] = [];
		}

		const item: IGuessGalleryItem = {
			year: dateObj.get("year"),
			gallery,
			yearMonthStr,
			yearMonthDateStr: dateObj.format("YYYY-MM-DD"),
			title: "",
			unique: true,
		};

		selGalleries.push(item);
		dates[yearMonthStr].push(item);
	}

	const allKeys = Object.keys(dates);

	for (const key of allKeys) {
		if (dates[key].length > 1) {
			for (const selGalleryItem of dates[key]) {
				selGalleryItem.unique = false;
			}
		}
	}

	for (const galleryItem of selGalleries) {
		galleryItem.title = `${galleryItem.unique ? galleryItem.yearMonthStr : galleryItem.yearMonthDateStr} ${galleryItem.gallery.event.title}`;
	}

	selGalleries.sort((aItem, bItem) => {
		if (aItem.year === bItem.year) {
			return aItem.title.localeCompare(bItem.title);
		}

		return aItem.year - bItem.year;
	});

	//console.log({ selTag, tagsObj });

	const selEventInd = getRandomArbitrary(0, selGalleries.length - 1);
	const selGallery = selGalleries[selEventInd];

	return {
		selGallery,
		selGalleries,
	};
}

interface IGuessGallery extends IGetRandomGalleryByTags {
	photosCount?: number;
}

export function guessGallery({
	photosCount = 1,
	...rest
}: IGuessGallery): IGuessData {
	// vybereme nahodny
	const { selGallery, selGalleries } = getRandomGalleryByTags({
		...rest,
	});

	// fotky
	const photos: Array<string> = selGallery.gallery.items.map(item => item.isVideo ? item.preview : item.largeSrc);
	const photosList = getRandomList(0, photos.length - 1, photosCount);
	const photosSrc: Array<string> = [];

	for (const ind of photosList) {
		photosSrc.push(photos[ind]);
	}

	if (photosList.length === 0) {
		for (const photo of photos) {
			photosSrc.push(photo);
		}
	}

	return {
		link: getGalleryLink(selGallery.gallery.event.seo, selGallery.gallery.event.date, selGallery.gallery.event.toDate),
		names: selGalleries.map(gallery => gallery.title),
		photos: photosSrc,
		selName: selGallery.title,
	};
}

interface IGetImageDebugLines {
	data: IGalleryItem;
	debugData?: IDebugStoreData;
	full?: boolean;
	showMeta?: boolean;
}

export function getImageDebugLines({
	data,
	debugData = null,
	full = false,
	showMeta = false,
}: IGetImageDebugLines) {
	let lines: Array<IDebugLine> = [];
	const metaData = data.eventSeo in debugData && data.name in debugData[data.eventSeo]
		? debugData[data.eventSeo][data.name]
		: {};

	lines.push({ text: `Name: ${data.name}${data.private ? ", privátní" : ""}` });
	full && lines.push({ text: `CrdsS: ${data.coordsSource}`, type: ["none", "event"].includes(data.coordsSource) ? "error" : "info" });
	full && lines.push({ text: `DateS: ${data.dateSource}`, type: ["none", "event"].includes(data.dateSource) ? "error" : "info" });

	if (!data.date) {
		lines.push({ text: `Chybí datum!`, type: "error" });
	}

	if (data.persons || metaData.persons) {
		const peoples = data.persons?.length > 0
			? getImagePersons({
				persons: data.persons,
				short: !full,
			})
			: getImagePersons({
				persons: [],
				names: metaData.persons,
				short: !full,
			});

		lines.push({ text: `Persons (${(data.persons || metaData.persons).length}): ${peoples}` });
	}

	if (data.tags) {
		lines.push({ text: `Tags: ${data.tags.join(", ")}` });
	}

	if (data.meta && showMeta) {
		const parts: Array<string> = [];

		if (data.meta.coords) {
			parts.push("C");
		}

		if (data.meta.date) {
			parts.push("D");
		}

		if (data.meta.customDayName) {
			parts.push("CDN");
		}

		if (data.meta.tags) {
			parts.push("T");
		}

		if (data.meta.private) {
			parts.push("PRI");
		}

		if (parts.length) {
			lines.push({ text: `Meta: ${parts.join(",")}` });
		}
	}


	if (Object.keys(metaData).length > 0) {
		const parts: Array<IDebugLine> = [];

		if (metaData.title) {
			parts.push({ text: `Title: ${metaData.title}` });
		}

		if (metaData.customDayName) {
			parts.push({ text: `Cdn: ${metaData.customDayName}` });
		}

		if (metaData.tags) {
			parts.push({ text: `Tags: ${metaData.tags.join(", ")}` });
		}

		if (metaData.coords) {
			parts.push({ text: `Coords: ${metaData.coords[0].toFixed(2)}, ${metaData.coords[1].toFixed(2)}` });
		}

		if (parts.length) {
			lines.push({ text: `Změny:` });
			lines = lines.concat(parts);
		}
	}

	return lines;
}

export function getGalleryDebugLines(gallery: IGallery) {
	const lines: Array<IDebugLine> = [];
	let personsCount = 0;
	let privateCount = 0;
	let missingCoords = 0;
	let missingDates = 0;
	const allDates: Array<string> = [];

	//console.log([gallery.event.title, gallery]);

	for (const item of gallery.items) {
		personsCount += item.persons ? 1 : 0;
		privateCount += item.meta?.private ? 1 : 0;
		missingCoords += ["none", "event"].includes(item.coordsSource) ? 1 : 0;
		missingDates += ["none", "event"].includes(item.dateSource) ? 1 : 0;

		if (item.dateSource === "image" && hasTimeHours(item.date)) {
			allDates.push(item.date);
		}
	}

	if (personsCount > 0) {
		lines.push({ text: `Osoby - ${personsCount} fotek` });
	} else {
		lines.push({ text: `Chybí osoby`, type: "error" });
	}

	if (privateCount > 0) {
		lines.push({ text: `Obsahuje private` });
	}

	if (missingCoords > 0) {
		lines.push({ text: `Chybí souřadnice ${missingCoords}`, type: gallery.event.coords ? "info" : "error" });
	}

	if (missingDates > 0) {
		lines.push({ text: `Chybí datumy ${missingDates}`, type: "error" });
	}

	if (gallery.datesRangeTo) {
		lines.push({ text: `Datum od: ${dayjs(gallery.datesRangeFrom).format(DAYJS_NICE_FORMAT_DATE)}` });
		lines.push({ text: `Datum do: ${dayjs(gallery.datesRangeTo).format(DAYJS_NICE_FORMAT_DATE)}` });
	} else {
		lines.push({ text: `Datum: ${dayjs(gallery.datesRangeFrom).format(DAYJS_NICE_FORMAT_DATE)}` });
	}

	if (allDates.length >= 2) {
		const first = allDates[0].slice(10);
		const last = allDates[allDates.length - 1].slice(10);

		lines.push({ text: `Čas od: ${first}` });
		lines.push({ text: `Čas do: ${last}` });
	} else if (allDates.length === 1) {
		lines.push({ text: `Čas: ${allDates[0].slice(10)}` });
	}

	if (gallery.event.tags?.length) {
		lines.push({ text: `Tagy: ${gallery.event.tags.join(", ")}` });
	}

	const skip: Array<string> = [];

	if (gallery.event.skipCalendar) {
		skip.push("kalendář");
	}

	if (gallery.event.skipThisDay) {
		skip.push("tento den");
	}

	if (skip.length > 0) {
		lines.push({ text: `Přeskočit: ${skip.join(", ")}` });
	}

	return lines;
}

export function parseNumber(value: string, minValue = 0): number {
	// zrusime - pro vsechny pripady
	const strValue = value.replace("-", "");
	const numValue = strValue
		? parseFloat(strValue)
		: minValue;

	return isNaN(numValue)
		? minValue
		: numValue;
}

export function getRepSigns(count: number, sign = " ") {
	return Array.from({ length: count }).fill(sign).join("");
}

export function getPersonName(personData: IPersonItem, curDate: string) {
	const curDateUnix = dayjs(curDate).unix();

	if (personData.maidenItems) {
		for (const maidenItem of personData.maidenItems) {
			const fromDate = maidenItem.fromDate
				? dayjs(maidenItem.fromDate).unix()
				: 0;
			const toDate = dayjs(maidenItem.toDate || undefined).unix();

			if (curDateUnix >= fromDate && curDateUnix <= toDate) {
				const name = personData.fullName.split(" ")[0];

				return `${name} ${maidenItem.surname}`;
			}
		}
	}

	return personData.fullName;
}

export function getPersonAgeStr(birthdate: string, checkDate?: string) {
	const birthdateObj = dayjs(birthdate);
	const checkDateObj = checkDate
		? dayjs(checkDate)
		: dayjs();
	const years = checkDateObj.diff(birthdateObj, "years");
	let dateStr = '';

	if (years < MIN_YEARS) {
		const months = checkDateObj.diff(birthdateObj, "months");

		if (months === 0) {
			const days = checkDateObj.diff(birthdateObj, "days");

			dateStr = `${days} d`;
		} else if (months < 12) {
			dateStr = `${months} m`;
		} else {
			const curYears = months / 12 >>> 0;
			const curMonths = months - (years * 12);

			dateStr = `${transYears(curYears)}${curMonths > 0 ? ` ${curMonths} m` : ""}`;
		}
	} else {
		dateStr = years.toString();
	}

	return dateStr;
}

export function getPersonsWithYears(persons: Array<IPersonItem>, eventDate: string) {
	const output: Array<string> = [];

	for (const data of persons) {
		const personName = getPersonName(data, eventDate);

		// zesnuly nebudou s vekem
		if (data.birthdate && eventDate && (!("deceased" in data) || dayjs(eventDate).unix() < dayjs(data.deceased).unix())) {
			const dateStr = getPersonAgeStr(data.birthdate, eventDate);

			output.push(`${personName} ${dateStr}`);
		} else {
			output.push(personName);
		}
	}

	return output;
}

export async function zipFiles(folderName: string, files: Array<IZipFileItem>) {
	const { default: JSZip } = await import(/* webpackChunkName: "jszip" */"jszip");
	const zip = new JSZip();
	const zipFolder = zip.folder(folderName);
	const allPromises = [];

	files.forEach(item => {
		allPromises.push(new Promise(resolve => {
			imgToArrayBuffer(item.src).then(imgData => {
				zipFolder.file(item.name.replace("webp", "jpg"), imgData);
				resolve({});
			}, () => resolve({}));
		}));
	});

	await Promise.all(allPromises);

	const zipData = await zip.generateAsync({ type: "blob" });
	const { default: saveAs } = await import("file-saver");

	saveAs(zipData, `${folderName}.zip`);
}

export function scrollToHeader() {
	const elem = document.querySelector(".mainContent__header");

	if (elem) {
		elem.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}
}

export function getEventDayId(day: IEventDayItem) {
	return `${day.type}-${day.customName || day.date || "none"}`;
}

export function getServiceMinDate(service: TService) {
	switch (service) {
		case "makuderovi":
			return MIN_DATE_MAKUDEROVI;

		case "roman":
			return MIN_DATE_ROMAN;

		case "lenka":
			return MIN_DATE_LENKA;

		default:
	}

	return MIN_DATE_MAKUDEROVI;
}

export function getEventDaysData(event: IEvent) {
	const firstDate = dayjs(event.date);
	const lastDate = dayjs(event.toDate || event.date);
	const daysCount = lastDate.diff(firstDate, "days") + 1;
	const nightsCount = daysCount - 1;
	const shortVersion = `${daysCount}/${nightsCount}`;

	return {
		daysCount,
		nightsCount,
		shortVersion,
	};
}

interface IDescItem {
	title: string;
	value: string;
}

export function getDayItems({
	day,
	photosCount = 0,
	videosCount = 0,
	tracksCount = 0,
	distances = null,
}: {
	day?: IEventDayItem;
	photosCount?: number;
	videosCount?: number;
	tracksCount?: number;
	distances?: ITrackDistances;
}) {
	const descItems: Array<IDescItem> = [];
	const photosCountVal = day?.photosCount || photosCount;
	const videosCountVal = day?.videosCount || videosCount;

	if (photosCountVal) {
		descItems.push({
			title: "počet fotek",
			value: photosCountVal.toString(),
		});
	}

	if (videosCountVal) {
		descItems.push({
			title: "počet videí",
			value: videosCountVal.toString(),
		});
	}

	if (tracksCount) {
		descItems.push({
			title: "počet tracků",
			value: tracksCount.toString(),
		});
	}

	if (distances) {
		if (distances.walk > 0) {
			descItems.push({
				title: "pěšky",
				value: formatDistance(distances.walk),
			});
		}

		if (distances.bike > 0) {
			descItems.push({
				title: "kolo",
				value: formatDistance(distances.bike),
			});
		}

		if (distances.car > 0) {
			descItems.push({
				title: "autem",
				value: formatDistance(distances.car),
			});
		}

		if (distances.boat > 0) {
			descItems.push({
				title: "lodí",
				value: formatDistance(distances.boat),
			});
		}

		if (distances.cable > 0) {
			descItems.push({
				title: "lanovkou",
				value: formatDistance(distances.cable),
			});
		}

		if (distances.pubt > 0) {
			descItems.push({
				title: "hromadná doprava",
				value: formatDistance(distances.pubt),
			});
		}

		if (distances.train > 0) {
			descItems.push({
				title: "vlakem",
				value: formatDistance(distances.train),
			});
		}
	}

	// prvni pismeno chceme velke
	if (descItems.length) {
		descItems[0].title = `${descItems[0].title[0].toLocaleUpperCase()}${descItems[0].title.slice(1)}`;
	}

	return descItems;
}
